import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppBadgeForAno, AppBadgeForID, AppIconButton, AppMemberDisplay } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Button } from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { SalesStageBadge } from '@opportunities/-components/SalesStageBadge'
import type { OpportunityMonthlySale } from 'backend/types'
import { Copy, ExternalLink } from 'lucide-react'
import { OPPORTUNITY_SALES_STAGE_MAP } from 'shared'
import { formatCurrency, formatDate } from '@/lib/format-utils'

interface CreateSalesColumnsParams {
  onCopy: (sale: OpportunityMonthlySale) => void
}

/**
 * 売上一覧のカラム定義を生成
 */
export function createSalesColumns({ onCopy }: CreateSalesColumnsParams): AppDataTableColumn<OpportunityMonthlySale>[] {
  return [
    {
      key: 'sales_id',
      label: '売上情報ID',
      accessor: (row) => row.sales_id,
      required: true,
      render: (row) => <AppBadgeForID value={row.sales_id} />,
    },
    {
      key: 'salesforce_customer_name',
      label: '取引先名',
      accessor: (row) => row.salesforce_customer_name,
      sortable: false,
      required: true,
      render: (row) => <span>{row.salesforce_customer_name || '-'}</span>,
    },
    {
      key: 'ano',
      label: 'ANO',
      accessor: (row) => row.ano,
      sortable: false,
      cellClassName: 'min-w-[100px] text-center',
      active: true,
      render: (row) => <AppBadgeForAno ano={row.ano} />,
    },
    {
      key: 'salesforce_project_name',
      label: 'ANOの案件名',
      accessor: (row) => row.salesforce_project_name,
      sortable: false,
      cellClassName: 'min-w-[250px]',
      active: true,
      render: (row) => (
        <span className='block' title={row.salesforce_project_name || undefined}>
          {row.salesforce_project_name || '-'}
        </span>
      ),
    },
    {
      key: 'salesforce_original_project_name',
      label: '売上情報の案件名',
      accessor: (row) => row.salesforce_original_project_name,
      sortable: false,
      cellClassName: 'min-w-[250px]',
      active: true,
      render: (row) => (
        <span className='block' title={row.salesforce_original_project_name || undefined}>
          {row.salesforce_original_project_name || '-'}
        </span>
      ),
    },
    {
      key: 'sales_stage_id',
      label: 'Sales Stage',
      accessor: (row) => row.sales_stage_id,
      cellClassName: 'text-center min-w-[80px]',
      active: true,
      render: (row) => <SalesStageBadge stageId={row.sales_stage_id} variant='opportunity' />,
    },
    {
      key: 'previously_closed_sales_stage_id',
      label: '失注前Sales Stage',
      accessor: (row) => row.previously_closed_sales_stage_id,
      cellClassName: 'text-center min-w-[80px]',
      active: true,
      render: (row) =>
        row.sales_stage_id === 11 ? (
          <SalesStageBadge stageId={row.previously_closed_sales_stage_id} variant='opportunity' />
        ) : (
          <span className='text-muted-foreground'>-</span>
        ),
    },
    {
      key: 'opportunity_amount',
      label: 'オポ金額',
      accessor: (row) => row.opportunity_amount,
      sortable: false,
      headerClassName: '[&>div]:justify-center',
      cellClassName: 'text-right min-w-[100px]',
      active: true,
      render: (row) => <span className='text-right'>{formatCurrency(row.opportunity_amount) || '-'}</span>,
      footer: (data) => {
        const total = data.reduce((sum, item) => sum + (item.opportunity_amount ?? 0), 0)
        return <span className='text-right text-xs'>合計: {formatCurrency(total)}</span>
      },
      footerClassName: 'text-right',
    },
    {
      key: 'amount',
      label: '売上金額',
      accessor: (row) => row.amount,
      sortable: false,
      headerClassName: '[&>div]:justify-center',
      cellClassName: 'text-right min-w-[100px]',
      active: true,
      render: (row) => <span className='text-right'>{formatCurrency(row.amount) || '-'}</span>,
      footer: (data) => {
        const total = data.reduce((sum, item) => sum + (item.amount ?? 0), 0)
        return <span className='text-right text-xs'>合計: {formatCurrency(total)}</span>
      },
      footerClassName: 'text-right',
    },
    {
      key: 'sales_on',
      label: '売上月',
      accessor: (row) => row.sales_on,
      sortable: false,
      cellClassName: 'text-center min-w-[100px]',
      active: true,
      render: (row) => <span>{formatDate(row.sales_on, 'salesMonth')}</span>,
    },
    {
      key: 'sales_information_owner_member_name',
      label: '売上情報所有者',
      accessor: (row) => row.sales_information_owner_member_name,
      sortable: false,
      cellClassName: 'min-w-[150px]',
      active: true,
      render: (row) => (
        <AppMemberDisplay
          employeeNumber={row.sales_information_owner_member_employee_number}
          name={row.sales_information_owner_member_name}
          emptyDisplay='dash'
        />
      ),
    },
    {
      key: 'salesforce_system_id',
      label: 'Link',
      accessor: (row) => row.salesforce_system_id,
      sortable: false,
      cellClassName: 'text-center min-w-[60px]',
      active: true,
      render: (row) =>
        row.salesforce_system_id ? (
          <Button variant='ghost' size='sm' asChild className='h-8 w-8 p-0'>
            <a
              href={`https://shiftinc.my.salesforce.com/${row.salesforce_system_id}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <ExternalLink className='h-4 w-4' />
            </a>
          </Button>
        ) : null,
    },
    {
      key: 'action',
      label: 'Action',
      accessor: () => null,
      sortable: false,
      headerClassName: '[&>div]:justify-center',
      cellClassName: 'text-center min-w-[80px]',
      active: true,
      render: (row) => (
        <AppIconButton onClick={() => onCopy(row)} description='CSV形式で行をクリップボードにコピー'>
          <Copy className='h-4 w-4' />
        </AppIconButton>
      ),
    },
  ]
}

/**
 * クリップボードコピー用のヘッダー定義
 */
export const SALES_HEADERS = [
  '売上情報ID',
  '取引先名',
  'ANOの案件名',
  '売上情報の案件名',
  'Sales Stage',
  '失注前Sales Stage',
  'オポ金額',
  '売上金額',
  '売上月',
  '売上情報所有者',
]

/**
 * クリップボードコピー用の値抽出関数
 */
export function extractSalesValues(sale: OpportunityMonthlySale) {
  return [
    sale.sales_id,
    sale.salesforce_customer_name || '',
    sale.ano || '',
    sale.salesforce_original_project_name || '',
    OPPORTUNITY_SALES_STAGE_MAP[sale.sales_stage_id]?.text || '',
    sale.previously_closed_sales_stage_id
      ? OPPORTUNITY_SALES_STAGE_MAP[sale.previously_closed_sales_stage_id]?.text || ''
      : '',
    sale.opportunity_amount?.toString() || '',
    sale.amount.toString(),
    formatDate(sale.sales_on, 'salesMonth'),
    sale.sales_information_owner_member_name || '',
  ]
}
